import { useRef, useMemo, useState, useEffect } from 'react';
import { Box, CircularProgress, Dialog, DialogTitle, DialogContent, IconButton, Stack, Tab, Tabs, Typography, Tooltip, } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import CloseIcon from '@mui/icons-material/Close';
import { OpenInNew, ReceiptLong } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import AGGridQuickFilterWrapper from '../../reuseable-components/AGGrid/AGGridQuickFilterWrapper';
import { AGGridDateFilter } from '../../reuseable-components/AGGrid/AGGridFilters';
import ExcelDownloadGridButton from '../../reuseable-components/ExcelInput/ExcelDownloadGridButton';
import axiosInstance from '../../utils/axios';
import AGGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';

export default function HistoryReport({ open, onClose, data }) {
  const gridRef = useRef(null);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);

  const highlightChangedText = (field) => (params) => {
    const current = params.value;
    const nextRow = params.api.getDisplayedRowAtIndex(params.node.rowIndex + 1);
    if (!nextRow) return current;
    const prev = nextRow?.data?.[field];
    const isEmpty = (x) => x == null || x === '' || (Array.isArray(x) && x.length === 0);
    const renderText = (text, changed) => <span style={{ color: changed ? 'blue' : 'inherit' }}>{text}</span>;
    if (!Array.isArray(current) && !Array.isArray(prev)) {
      if (isEmpty(current) && !isEmpty(prev)) return renderText('Deleted value', true);
      if (current !== prev) return renderText(current || 'Deleted value', true);
      return current;
    }
    const currArr = Array.isArray(current) ? current : [];
    const prevArr = Array.isArray(prev) ? prev : [];
    const maxLength = Math.max(currArr.length, prevArr.length);
    return Array.from({ length: maxLength }, (_, i) => {
      const changed = currArr[i] !== prevArr[i];
      return (
        <span key={i}>{renderText(currArr[i] ?? 'Deleted value', changed)}{i < maxLength - 1 ? ',\u00A0' : ''}</span>
      );
    });
  };

  const formattedDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '-');
  const tabNameMap = { 0: 'OrderHistory', 1: 'ItemHistory', 2: 'CustomerPricingHistory' };
  const fileName = `Product${data?.id}-${tabNameMap[tab]}-${formattedDate}.xlsx`;

  const columnDefsMap = useMemo(() => ({
    0: [
      {
        field: 'orderId',
        headerName: 'PO#',
        filter: true,
        minWidth: 100,
        flex: 1,
        cellRenderer: (params) => <>
          {params.data.orderId}
          {params.data.invoices && params.data.invoices.length > 0 && <Tooltip title={<Stack spacing={1}>
            {params.data.invoices.map((invoice, index) => <Box key={index}>
              {invoice.value}
              <IconButton onClick={() => window.open(`/admin/invoices?id=${invoice.key}`, "_blank")} size="small" disableRipple>
                <OpenInNew />
              </IconButton>
            </Box>)}
          </Stack>} arrow placement="top">
            <IconButton edge="end" color="secondary" disableRipple sx={{ px: 0, my: 0, mr: 1 }}>
              <ReceiptLong />
            </IconButton>
          </Tooltip>
          }
        </>
      },
      {
        field: 'po', headerName: 'Customer PO#', filter: true, minWidth: 150, flex: 1
      },
      {
        field: 'facility', headerName: 'Facility', filter: true, minWidth: 150
      },
      {
        field: 'group', headerName: 'Group', filter: true, minWidth: 150
      },
      {
        field: 'dateApproved', headerName: 'Approved On', cellRenderer: (p) => p.value ? new Date(p.value).toDateString() : '', ...AGGridDateFilter(), minWidth: 150
      },
      {
        field: 'vendor', headerName: 'Vendor', filter: true, minWidth: 150
      },
      {
        field: 'vendorItemId', headerName: 'Vendor Item Id', filter: true, minWidth: 150
      },
      {
        field: 'description', headerName: 'Description', filter: true, minWidth: 150
      },
      {
        field: 'vendorPrice', headerName: 'Vendor Price', cellRenderer: (p) => p.value?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }), filter: "agNumberColumnFilter", minWidth: 150
      },
      {
        field: 'price', headerName: 'Price', cellRenderer: (p) => p.value?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }), filter: "agNumberColumnFilter", minWidth: 150
      },
      {
        field: 'quantity', headerName: 'Quantity', filter: "agNumberColumnFilter", minWidth: 150
      },
      {
        field: 'total', headerName: 'Total', cellRenderer: (p) => p.value?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }), filter: "agNumberColumnFilter", minWidth: 150
      },
      {
        field: 'orderer', headerName: 'Submitted By', filter: true, minWidth: 150
      },
    ],
    1: [
      {
        field: 'productId', headerName: 'Product ID', filter: true, minWidth: 150, flex: 1
      },
      {
        field: 'name', headerName: 'Name', filter: true, minWidth: 150, cellRenderer: highlightChangedText('name'),
      },
      {
        field: 'description', headerName: 'Description', filter: true, minWidth: 150, cellRenderer: highlightChangedText('description'),
      },
      {
        field: 'productCategory', headerName: 'Product Category', filter: true, minWidth: 150, cellRenderer: highlightChangedText('productCategory'),
      },
      {
        field: 'uomTypes', headerName: 'UOM/s', filter: true, minWidth: 150, cellRenderer: highlightChangedText('uomTypes')
      },
      {
        field: 'vendor', headerName: 'Vendor', filter: true, minWidth: 150, cellRenderer: highlightChangedText('vendor'),
      },
      {
        field: 'vendorItemId', headerName: 'Vendor Item Id', filter: true, minWidth: 150, cellRenderer: highlightChangedText('vendorItemId'),
      },
      {
        field: 'manufacturer', headerName: 'Manufacturer', filter: true, minWidth: 150, cellRenderer: highlightChangedText('manufacturer'),
      },
      {
        field: 'manufacturerId', headerName: 'Manufacturer SKU', filter: true, minWidth: 150, cellRenderer: highlightChangedText('manufacturerId'),
      },
      {
        field: 'manufacturerLink', headerName: 'Manufacturer Link', filter: true, minWidth: 150, cellRenderer: highlightChangedText('manufacturerLink'),
      },
      {
        field: 'dateAdded', headerName: 'Date Updated', cellRenderer: (p) => p.value ? new Date(p.value).toLocaleDateString() : '', ...AGGridDateFilter(), minWidth: 150
      },
      {
        field: 'addedBy', headerName: 'Updated By', filter: true, minWidth: 150,
      },
    ],
    2: [
      {
        field: 'productId', headerName: 'Product ID', filter: true, minWidth: 200
      },
      {
        field: 'purchasingGroup', headerName: 'Facility / Purchasing Group', filter: true, minWidth: 250, flex: 1,
      },
      {
        field: 'price', headerName: 'Price', cellRenderer: (p) => p.value?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }), filter: "agNumberColumnFilter", minWidth: 200
      },
      {
        field: 'uomType', headerName: 'UOM', filter: true, minWidth: 200,
      },
      {
        field: 'dateAdded', headerName: 'Date Updated', cellRenderer: (p) => p.value ? new Date(p.value).toLocaleDateString() : '', ...AGGridDateFilter(), minWidth: 200
      },
      {
        field: 'addedBy', headerName: 'Updated By', filter: true, minWidth: 200,
      },
      {
        field: 'action', headerName: 'Item Status', filter: true, minWidth: 200,
      },

    ],
  }), []);

  useEffect(() => {
    if (!open)
      setTab(0);
    if (!open || !data) return;
    setLoading(true);
    setRowData([]);
    const endpoints = [ `/order/history?id=${data.id}`, `/product/history?id=${data.id}`, `/orderGuide/history?id=${data.id}`];
    axiosInstance.get(endpoints[tab])
      .then((res) => {
        setRowData(res.data);
      })
      .catch(() => enqueueSnackbar('Failed to fetch history', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [open, data, tab]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <Typography variant="h6">History</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: '#fafafa' }}>
        <Tabs value={tab} onChange={(e, newValue) => setTab(newValue)} sx={{ mb: 2 }}>
          <Tab label="Order History" />
          <Tab label="Item History" />
          <Tab label="Customer Pricing History" />
        </Tabs>
        <AGGridQuickFilterWrapper gridRef={gridRef}>
          <Stack direction="row" spacing={2}>
            <ExcelDownloadGridButton gridRef={gridRef} fileName={fileName} worksheetName={tabNameMap[tab]} formatMap={{
              dateApproved: { type: "date" }, dateAdded: { type: "date" }, vendorPrice: { type: "currency", currency: "USD" },
              price: { type: "currency", currency: "USD" }, total: { type: "currency", currency: "USD" }, quantity: { type: "integer" }
            }} />
          </Stack>
        </AGGridQuickFilterWrapper>
        {!loading ? (
          <Box sx={{ height: 400, mt: 2, width: '100%', overflowX: 'auto' }}>
            <AgGridReact
              {...AGGridDefaults}
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefsMap[tab]}
              domLayout="normal"
              defaultColDef={{ resizable: true, }}
              suppressHorizontalScroll={false}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', m: 3 }}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
